<template>
  <Dialog v-model:visible="visible" modal :draggable="false" :closable="false" style="min-width: 720px">
    <template #header>
      <h2>New App Pricing Template</h2>
    </template>
    <Form
      v-slot="$form"
      :initial-values="initialValues"
      style="display: flex; flex-direction: column; gap: 16px"
      :resolver="schema"
      @submit="onFormSubmit"
    >
      <FormField>
        <label for="pricingTemplateName">Template Name</label>
        <InputGroup>
          <InputText
            id="pricingTemplateName"
            type="text"
            name="pricingTemplateName"
            placeholder="Enter name of template"
            required
          />
          <Message v-if="$form.pricingTemplateName?.invalid" severity="error" size="small" variant="simple">
            {{ $form.pricingTemplateName.error.message }}
          </Message>
        </InputGroup>
      </FormField>
      <div>
        <label for="templateDefaultPrice">
          Base price
          <span :class="$style.taxHint">tax excl. — Google Play adds local taxes per country</span>
        </label>
        <InputGroup>
          <InputGroupAddon>
            <Select
              id="defaultCurrencyCode"
              name="defaultCurrencyCode"
              :options="availableDefaultCurrencies"
              class="w-8rem"
            />
          </InputGroupAddon>
          <InputNumber
            id="templateDefaultPrice"
            name="templateDefaultPrice"
            mode="currency"
            :currency="$form.defaultCurrencyCode?.value ?? 'EUR'"
            fluid
          />
        </InputGroup>
      </div>
      <div :class="$style.formButtons">
        <Button type="submit" label="Create" :loading="loading" />
        <Button type="button" label="Cancel" severity="secondary" :disabled="loading" @click="onCancel()" />
      </div>
    </Form>
  </Dialog>
</template>

<script setup lang="ts">
import { InputGroup, InputGroupAddon, Select, InputText, InputNumber, Dialog, Button, Message } from "primevue";
import { Form, FormField } from "@primevue/forms";
import { ref, defineEmits, defineModel } from "vue";
import * as yup from "yup";
import usePricingTemplates from "@/modules/IAP/hooks/usePricingTemplates";
import { useRoute } from "vue-router";
import { yupResolver } from "@primevue/forms/resolvers/yup";
import useToast from "@/hooks/useToastNotification";

const route = useRoute();
const { toastSuccess } = useToast();
const emit = defineEmits(["submit"]);
const { createPricingTemplate } = usePricingTemplates();
const availableDefaultCurrencies = ref(["EUR", "USD"]);
const gameId = ref(route.params.gameId);
const loading = ref(false);
const visible = defineModel("visible");
const schema = ref(
  yupResolver(
    yup.object({
      pricingTemplateName: yup.string().required("A name is required"),
      templateDefaultPrice: yup.string().required("A default value is required"),
      defaultCurrencyCode: yup.string().oneOf(availableDefaultCurrencies.value),
    })
  )
);

const initialValues = {
  defaultCurrencyCode: availableDefaultCurrencies.value[0],
};

function onCancel() {
  visible.value = false;
}

const onFormSubmit = async (form) => {
  if (form.valid) {
    loading.value = true;
    const result = await createPricingTemplate(gameId.value as string, form.values);
    loading.value = false;
    if (result) {
      toastSuccess({
        detail: `Successfully created new pricing template ${form.values.pricingTemplateName}`,
        summary: "Success",
        life: 5000,
      });
      visible.value = false;
      emit("submit");
    }
  }
};
</script>

<style module>
.taxHint {
  margin-left: 4px;
  font-size: 12px;
  color: var(--vd-color-text-default);
}

.formButtons {
  display: flex;
  gap: 8px;
  flex-direction: row-reverse;
}
</style>

<style scoped>
:deep(.p-message-text) {
  max-width: 660px;
}
</style>
